// src/routes/marketplace-routes.tsx - REFACTORED VERSION
import type { RoutesProps } from './routes';

// Import sub-modules
import { 
  marketplaceCoreRoutes, 
  marketplaceCoreNavigationStructure,
  hasAccessToMarketplaceCore,
  canPublishContent,
  canBuyLicenses 
} from './marketplace/marketplace-core-routes';

import { 
  marketplaceCreatorRoutes,
  marketplaceCreatorNavigationStructure,
  canAccessCreatorTools,
  getCreatorMenuGroup 
} from './marketplace/marketplace-creator-routes';

import { 
  marketplacePreLicenseRoutes,
  marketplacePreLicenseNavigationStructure,
  canAccessPreLicense,
  getPreLicenseMenuGroup 
} from './marketplace/marketplace-prelicense-routes';

import { 
  marketplaceUnclaimedRoutes,
  marketplaceUnclaimedNavigationStructure,
  canAccessUnclaimedContent,
  canAccessVaultContent,
  canAccessOrphanContent,
  canSubmitClaims,
  canReviewClaims,
  getAuthorFinderMenuGroup 
} from './marketplace/marketplace-unclaimed-routes';

// Combine all marketplace routes
export const marketplaceRoutes: RoutesProps[] = [
  ...marketplaceCoreRoutes,
  ...marketplaceCreatorRoutes,
  ...marketplacePreLicenseRoutes,
  ...marketplaceUnclaimedRoutes,
];

// Combine all navigation structures
export const marketplaceNavigationStructure = {
  // Core marketplace
  ...marketplaceCoreNavigationStructure,
  
  // Creator tools
  ...marketplaceCreatorNavigationStructure,
  
  // Pre-license system
  ...marketplacePreLicenseNavigationStructure,
  
  // Unclaimed content (Author Finder)
  ...marketplaceUnclaimedNavigationStructure,
};

// === MAIN HELPER FUNCTIONS ===

export const getMarketplaceRoutesForRole = (userRoles: string[]): RoutesProps[] => {
  return marketplaceRoutes.filter((route) => {
    if (!route.roles || route.roles.length === 0) return true;
    return route.roles.some((role) => userRoles.includes(role));
  });
};

export const getMarketplaceNavigationForRole = (userRoles: string[]) => {
  const navigation: any[] = [];

  Object.entries(marketplaceNavigationStructure).forEach(([key, item]) => {
    if (item.roles && item.roles.some((role) => userRoles.includes(role))) {
      navigation.push(item);
    }
  });

  return navigation;
};

// === PERMISSION HELPER FUNCTIONS ===
export const hasAccessToMarketplace = (userRoles: string[]): boolean => {
  return hasAccessToMarketplaceCore(userRoles) ||
         canAccessCreatorTools(userRoles) ||
         canAccessPreLicense(userRoles) ||
         canAccessUnclaimedContent(userRoles);
};

// Re-export individual permission functions
export {
  hasAccessToMarketplaceCore,
  canPublishContent,
  canBuyLicenses,
  canAccessCreatorTools,
  canAccessPreLicense,
  canAccessUnclaimedContent,
  canAccessVaultContent,
  canAccessOrphanContent,
  canSubmitClaims,
  canReviewClaims,
};

// === MENU GROUPS ===
export const getMarketplaceMenuGroups = (userRoles: string[]) => {
  const menuGroups: any = {};

  // Public marketplace (for everyone including guests)
  if (hasAccessToMarketplaceCore(userRoles)) {
    menuGroups.marketplace = {
      key: 'marketplace-public',
      translationKey: 'marketplace.public',
      icon: marketplaceCoreNavigationStructure.home.icon,
      children: [
        marketplaceCoreNavigationStructure.home,
        marketplaceCoreNavigationStructure.browse,
      ].filter(item => item.roles.some(role => userRoles.includes(role)))
    };

    // Content owner features
    if (canPublishContent(userRoles)) {
      menuGroups.contentOwner = {
        key: 'marketplace-content-owner',
        translationKey: 'marketplace.contentOwner',
        icon: marketplaceCoreNavigationStructure.dashboard.icon,
        children: [
          marketplaceCoreNavigationStructure.dashboard,
          marketplaceCoreNavigationStructure.publishing,
          marketplaceCoreNavigationStructure.licenses,
        ]
      };
    }

    // License management (for buyers)
    if (canBuyLicenses(userRoles)) {
      menuGroups.licensing = {
        key: 'marketplace-licensing',
        translationKey: 'marketplace.licensing',
        icon: marketplaceCoreNavigationStructure.licenseRequest.icon,
        children: [
          marketplaceCoreNavigationStructure.licenseRequest,
          marketplaceCoreNavigationStructure.myRequests,
        ]
      };
    }
  }

  // Creator tools submenu
  const creatorMenu = getCreatorMenuGroup(userRoles);
  if (creatorMenu) {
    menuGroups.creatorTools = creatorMenu;
  }

  // Pre-license system
  const preLicenseMenu = getPreLicenseMenuGroup(userRoles);
  if (preLicenseMenu) {
    menuGroups.preLicense = preLicenseMenu;
  }

  // Author Finder (Unclaimed content)
  const authorFinderMenu = getAuthorFinderMenuGroup(userRoles);
  if (authorFinderMenu) {
    menuGroups.authorFinder = authorFinderMenu;
  }

  return menuGroups;
};

// Export default
export default marketplaceRoutes;